// Pure matching logic for "Говорить" exercises: given a chunk of recognized
// text and the small set of candidate answers for the current question,
// decide which candidate (if any) the user said. Kept separate from
// speechRecognizer.js so the matching rules can be tested/tuned without
// touching the Vosk/Kaldi plumbing.

export function normalizeText(str) {
  return (str || '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^а-яa-z0-9 ]/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Word-overlap score between recognized text and a candidate phrase (0..1).
// A single-word candidate only scores 1 when that exact word is present;
// a multi-word candidate (e.g. "Птица залетает в клетку") scores by the
// fraction of its words that were heard, so partial phrase matches count.
export function scoreAgainstCandidate(recognizedText, candidateText) {
  const candidateWords = normalizeText(candidateText).split(' ').filter(Boolean);
  if (candidateWords.length === 0) return 0;
  const recognizedWords = new Set(normalizeText(recognizedText).split(' ').filter(Boolean));
  const matched = candidateWords.filter((w) => recognizedWords.has(w)).length;
  return matched / candidateWords.length;
}

// candidates: [{ key, text }]. Returns the best-scoring candidate as
// { key, score } if it clears the threshold, otherwise null (meaning: not
// enough was recognized yet to call it — caller should keep listening).
export function matchCandidate(recognizedText, candidates, { threshold = 0.5 } = {}) {
  let best = null;
  for (const candidate of candidates) {
    const score = scoreAgainstCandidate(recognizedText, candidate.text);
    if (score > 0 && (!best || score > best.score)) {
      best = { key: candidate.key, score };
    }
  }
  return best && best.score >= threshold ? best : null;
}

// Word-set helpers for exercises that need to accumulate speech across
// several recognizer chunks (Vosk emits one 'result' per pause, so a
// multi-word answer said with pauses between words arrives as separate
// chunks) and decide once the accumulated words exactly match a candidate —
// see Clocks.jsx for the full accumulation pattern this supports.
export function wordSetOf(text) {
  return new Set(normalizeText(text).split(' ').filter(Boolean));
}

// a ⊆ b
export function isSubsetOf(a, b) {
  return [...a].every((w) => b.has(w));
}

export function setsEqual(a, b) {
  return a.size === b.size && isSubsetOf(a, b);
}

// For exercises where "nothing recognized in time" still needs to resolve to
// some concrete choice (e.g. a zone must be clicked/highlighted either way):
// pick any candidate key other than the correct one, so a timeout counts as
// an incorrect answer rather than silently freezing the exercise.
export function fallbackWrongKey(candidates, correctKey) {
  const other = candidates.find((c) => c.key !== correctKey);
  return other ? other.key : null;
}
